import React, { useState, useEffect, useMemo, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useStudent } from "../context/StudentContext";
import { obtenerMiTemarioCurso } from "../utils/acciones";
import { listOrderedCourseTabs, normalizeCourseContenido, resolvePublishedCourseTabsAndContenido } from "../utils/normalizeCourseShape";
import { API_URL } from "../config";
import { parseNavPathFromSearch, parseStudentNavQuery } from "../utils/studentNavPaths";
import TopicViewer from "../components/TopicViewer/TopicViewer";
import Icon from "../components/Icon/Icon";
import CourseDescriptionContent from "../components/CourseDescriptionContent";
import CoursePestanasNav from "../components/CoursePestanasNav";
import { getCourseDescriptionPlainTextLength } from "../utils/courseDescriptionHtml";
import {
  mergeTestStructureIntoCampusCourse,
  resolveTestStructureNavPath,
} from "../utils/studentTestCampusContent";
import { resolveCourseTestsCatalog } from "../utils/filterTemarioTestsForTab";
import { getMessage } from "../data/locales";
import "./Temario.css";

const CAMPUS_DESC_THRESHOLD = 420;
const EMPTY_PROGRESS = { percentage: 0, completed: 0, total: 0 };
const campusCourseCache = new Map();

export function buildCampusCourse(courseData, fallbackCourseId) {
  const cid = courseData.cursoId ?? fallbackCourseId;
  const contenido = normalizeCourseContenido(courseData.contenido || {});
  const published = resolvePublishedCourseTabsAndContenido(
    Array.isArray(courseData.pestanasCurso) ? courseData.pestanasCurso : [],
    contenido,
  );
  return {
    _id: cid,
    id: cid,
    titulo: courseData.titulo ?? "",
    descripcion: courseData.descripcion ?? "",
    imagen: courseData.imagen ?? "",
    pestanasCurso: published.pestanasCurso.length > 0 ? published.pestanasCurso : ["Programa Formativo"],
    contenido: published.contenido,
    contenidoDescargable: courseData.contenidoDescargable === true,
    tests: courseData.tests,
  };
}

async function fetchCampusProgress(courseId) {
  const resProgreso = await fetch(`${API_URL}/api/cursos/${courseId}/mi-progreso`, { credentials: 'include' });
  if (!resProgreso.ok) {
    return EMPTY_PROGRESS;
  }
  const progData = await resProgreso.json();
  return {
    percentage: progData.porcentajeAvance ?? 0,
    completed: progData.temasDominados ?? 0,
    total: progData.totalTemas ?? 0
  };
}

async function fetchCampusCourseAndProgress(courseId) {
  const [courseResult, progressResult] = await Promise.allSettled([
    obtenerMiTemarioCurso(courseId, { force: true }),
    fetchCampusProgress(courseId),
  ]);
  return {
    courseData: courseResult.status === "fulfilled"
      ? courseResult.value
      : { error: "error-red", message: getMessage("campus.temarioError.error-red") },
    progressData: progressResult.status === "fulfilled"
      ? progressResult.value
      : EMPTY_PROGRESS,
  };
}

function resolveTemarioErrorMessage(courseData) {
  if (!courseData || typeof courseData !== "object") {
    return getMessage("campus.temarioError.error-red");
  }
  const code = courseData.error;
  if (typeof code !== "string") {
    return getMessage("campus.temarioError.error-servidor");
  }
  if (typeof courseData.message === "string" && courseData.message.trim().length > 0) {
    return courseData.message;
  }
  const fallback = getMessage(`campus.temarioError.${code}`);
  return fallback.startsWith("campus.temarioError.") ? getMessage("campus.temarioError.error-servidor") : fallback;
}

function Campus() {
  const { activeCourse, activeCourseNavigation } = useStudent();
  const [searchParams] = useSearchParams();
  const pestanaFromUrl = searchParams.get("pestana") || "";
  const navQuery = parseStudentNavQuery(searchParams.toString());
  const navPathFromUrl = parseNavPathFromSearch(searchParams.toString());
  const focusArchivo = navQuery.archivo;
  const viewerRef = useRef(null);
  const [fullCourse, setFullCourse] = useState(null);
  const [loading, setLoading] = useState(false);
  const [progressData, setProgressData] = useState(EMPTY_PROGRESS);
  const [courseError, setCourseError] = useState(null);
  const [descExpanded, setDescExpanded] = useState(false);
  const courseDescId = fullCourse?._id
    ? `campus-desc-${fullCourse._id}`
    : "campus-desc";

  useEffect(() => {
    let isCurrent = true;

    const resetCampusState = (message = null) => {
      setFullCourse(null);
      setProgressData(EMPTY_PROGRESS);
      setCourseError(message);
    };

    const showCachedCourse = (cachedCourse) => {
      setFullCourse(cachedCourse.fullCourse);
      setProgressData(cachedCourse.progressData);
      setCourseError(null);
      setLoading(false);
    };

    const prepareUncachedCourse = () => {
      setLoading(true);
      resetCampusState(null);
    };

    const fetchCourseDetails = async () => {
      const courseId = activeCourse?._id;
      if (!courseId) {
        resetCampusState(null);
        return;
      }

      const cachedCourse = campusCourseCache.get(courseId);
      if (cachedCourse) {
        showCachedCourse(cachedCourse);
      } else {
        prepareUncachedCourse();
      }
      setDescExpanded(false);
      try {
        const { courseData, progressData: progressFromApi } = await fetchCampusCourseAndProgress(courseId);

        if (!courseData || courseData.error) {
          if (!isCurrent) return;
          if (cachedCourse) {
            return;
          }
          resetCampusState(resolveTemarioErrorMessage(courseData));
          return;
        }

        const transformedCourse = buildCampusCourse(courseData, courseId);
        if (!isCurrent) return;
        campusCourseCache.set(courseId, {
          fullCourse: transformedCourse,
          progressData: progressFromApi,
        });
        setProgressData(progressFromApi);
        setFullCourse(transformedCourse);
      } catch (err) {
        console.error("Error cargando el campus:", err);
        if (!isCurrent) return;
        if (cachedCourse) {
          return;
        }
        resetCampusState(getMessage("campus.temarioError.error-red"));
      } finally {
        if (isCurrent) setLoading(false);
      }
    };

    fetchCourseDetails();
    return () => { isCurrent = false; };
  }, [activeCourse?._id]);

  const directTabMode = pestanaFromUrl.trim().length > 0;
  const campusCourse = useMemo(
    () => {
      const merged = mergeTestStructureIntoCampusCourse(
        fullCourse,
        activeCourseNavigation?.testEstructura,
        listOrderedCourseTabs(
          fullCourse?.pestanasCurso?.length
            ? fullCourse.pestanasCurso
            : (activeCourseNavigation?.pestanasCurso?.length
              ? activeCourseNavigation.pestanasCurso
              : activeCourse?.pestanasCurso),
        ),
      );
      if (!merged) {
        return merged;
      }
      return {
        ...merged,
        tests: resolveCourseTestsCatalog(
          fullCourse?.tests ?? merged.tests,
          activeCourseNavigation?.testEstructura,
        ),
      };
    },
    [
      fullCourse,
      activeCourse?.pestanasCurso,
      activeCourseNavigation?.pestanasCurso,
      activeCourseNavigation?.testEstructura,
    ],
  );
  const focusNavPath = useMemo(() => {
    if (navPathFromUrl.length > 0) {
      return navPathFromUrl;
    }
    return resolveTestStructureNavPath(
      campusCourse,
      pestanaFromUrl,
      navQuery.bloque,
      navQuery.tema,
    );
  }, [campusCourse, navPathFromUrl, navQuery.bloque, navQuery.tema, pestanaFromUrl]);

  return (
    <div className={`cds-campus-root${directTabMode ? " cds-campus-root--direct-tab" : ""}`}>
      {directTabMode ? null : (
        <div className="cds-campus-header">
          <h1>Campus Virtual</h1>
          <p>Tu espacio personal de estudio y progreso</p>
        </div>
      )}

      {!activeCourse && !loading && (
        <div className="cds-campus-empty">
          <Icon name="book" className="cds-campus-icon cds-campus-icon-big" />
          <h3>¡Bienvenido a tu Academia!</h3>
          <p>Selecciona un curso en el menú para comenzar.</p>
        </div>
      )}

      {loading && (
        <div className="cds-campus-loading">
          <div className="cds-campus-spinner"></div>
          <p>Preparando material...</p>
        </div>
      )}

      {courseError && activeCourse && !loading && (
        <div className="cds-campus-empty" role="alert">
          <Icon name="book" className="cds-campus-icon" />
          <h3>{getMessage("campus.temarioErrorTitle")}</h3>
          <p>{courseError}</p>
        </div>
      )}

      {campusCourse && !loading && (
        <div className="cds-campus-layout">
          {directTabMode ? null : (
            <div className="cds-campus-hero-card">
              <div className="cds-hero-main">
                <span className="cds-badge-active">Curso Activo</span>
                <h2 className="cds-hero-title">{campusCourse.titulo}</h2>
                {campusCourse.descripcion ? (
                  <>
                    <CourseDescriptionContent
                      id={courseDescId}
                      html={campusCourse.descripcion}
                      className="cds-hero-desc"
                      clamped={
                        getCourseDescriptionPlainTextLength(campusCourse.descripcion) > CAMPUS_DESC_THRESHOLD
                        && !descExpanded
                      }
                    />
                    {getCourseDescriptionPlainTextLength(campusCourse.descripcion) > CAMPUS_DESC_THRESHOLD ? (
                      <button
                        type="button"
                        className="cds-hero-desc-toggle"
                        onClick={() => setDescExpanded((prev) => !prev)}
                        aria-expanded={descExpanded}
                        aria-controls={courseDescId}
                      >
                        {descExpanded ? "Ver menos" : "Ver más"}
                      </button>
                    ) : null}
                  </>
                ) : null}
                <button
                  type="button"
                  className="cds-btn-continue"
                  onClick={() => {
                    if (viewerRef.current) {
                      viewerRef.current.scrollIntoView?.({ behavior: "smooth", block: "start" });
                    }
                  }}
                >
                  <Icon name="play" className="cds-campus-icon" /> Continuar Estudio
                </button>
              </div>
              
              <div className="cds-hero-progress-box">
                <div className="cds-progress-top">
                  <Icon name="award" className="cds-campus-icon cds-award-icon" />
                  <span className="cds-progress-percent">{progressData.percentage}%</span>
                </div>
                <p className="cds-progress-label">Progreso Real</p>
                <div className="cds-progress-bar-bg">
                  <div className="cds-progress-bar-fill" style={{ width: `${progressData.percentage}%` }}></div>
                </div>
                <div className="cds-progress-stats">
                  <span>{progressData.completed} de {progressData.total} temas dominados</span>
                </div>
              </div>
            </div>
          )}

          <div className="cds-campus-viewer-wrapper" ref={viewerRef}>
            {directTabMode ? (
              <header className="cds-campus-direct-header">
                <Link to="/usuario/campus" className="cds-campus-direct-back">
                  {getMessage("campus.backToCampus")}
                </Link>
                <h1>{campusCourse.titulo}</h1>
                <p>{getMessage("campus.courseContentSubtitle")}</p>
                <CoursePestanasNav />
              </header>
            ) : null}
            <TopicViewer
              curso={campusCourse}
              initialPestana={pestanaFromUrl}
              focusNavPath={focusNavPath}
              focusArchivo={focusArchivo}
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default Campus;
